import { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import Page from "../Page";
import { useOnboarding } from "@/hooks/useOnboarding";
import Button from "../Button";
import styles from "./Step.module.scss";
import Chevron from "@/assets/icons/chevron.svg?react";
import {
  Answer,
  Answers,
  OnboardingStep,
  Option,
} from "@/data/onboarding/types";
import { calculateBmi, randomPick, toNumber } from "@/utils";
import { getStoredQueryParam } from "@/utils/queryParams";
import { FIND_PHARMACY_URL } from "@/constants";
import useStaleCopy from "@/hooks/useStaleCopy";
import bpImageSrc from "@/assets/bp-station.png";
import apotekImageSrc from "@/assets/apotek-hjartat.png";
import CheckBox from "../Checkbox";
import useDevice from "@/hooks/useDevice";
import ButtonWithDetails from "../ButtonWithDetails";
import { getFlowConfig, getFlowForStep } from "@/data/flows";

export type { StepProps } from "@/data/onboarding/types";

type Props = {
  landing?: boolean;
};

export default function Step({ landing }: Props) {
  const { slug } = useParams();
  const {
    getOnboardingStepBySlug,
    getOnboardingStepById,
    setStep,
    answerQuestion,
    answers,
    completeQuestionnaire,
    currentOnboardingFlow,
    setCurrentFlow,
  } = useOnboarding();
  const navigate = useNavigate();
  const currentStep = getOnboardingStepBySlug(slug);

  useEffect(() => {
    if (slug) {
      setStep(slug);
    }
  }, [slug, setStep]);

  const handleAnswer = (answer?: Answer) => {
    if (!currentStep) {
      return;
    }

    answerQuestion(answer || 0);
    const newAnswers = {
      ...answers,
      [currentStep.id]: answer,
    } as Answers;
    const nextStepId = currentStep?.nextStep(newAnswers);
    const flowConfig = getFlowConfig(currentOnboardingFlow);

    if (nextStepId) {
      if (!currentOnboardingFlow) {
        const detectedFlow = getFlowForStep(nextStepId);
        if (detectedFlow) {
          setCurrentFlow(detectedFlow.id);
        }
      }

      const nextSlug = getOnboardingStepById(nextStepId)?.slug;
      navigate(`/steg/${nextSlug}`);
    } else {
      if (
        getStoredQueryParam("collect_email") !== undefined &&
        currentStep.id !== "emailCollection" &&
        flowConfig?.treatmentArea === "OBESITY"
      ) {
        navigate("/steg/epost");
        return;
      }

      completeQuestionnaire();

      const useAB = flowConfig?.useRecommendationABTest?.(newAnswers);

      if (useAB) {
        navigate(
          randomPick([
            `/rekommendation?flow=${currentOnboardingFlow}`,
            `/rekommendation-c?flow=${currentOnboardingFlow}`,
          ]),
        );
      } else {
        navigate(`/rekommendation?flow=${currentOnboardingFlow}`);
      }
    }
  };

  if (
    !currentStep ||
    (!currentStep.allowDirectAccess && !Object.keys(answers).length)
  ) {
    const flowConfig = getFlowConfig(currentOnboardingFlow);
    return <Navigate to={flowConfig?.landingPath || "/"} replace />;
  }

  if (landing && currentStep.type !== "information") {
    return <Navigate to="/" replace />;
  }

  return (
    <Page justifyContent="flex-end">
      {currentStep.text && <h1>{currentStep.text}</h1>}
      {currentStep.subText && <p>{currentStep.subText}</p>}
      {currentStep.clarification && (
        <div>
          {typeof currentStep.clarification === "string" ? (
            currentStep.clarification
          ) : (
            <ul>
              {currentStep.clarification.map((row, i) => (
                <li key={i}>{row}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {import.meta.env.DEV ? <small>Debug: {currentStep.id}</small> : null}
      <StepVariant step={currentStep} onAnswer={handleAnswer} />
      {currentStep.disclaimer && (
        <div className={styles.disclaimer}>{currentStep.disclaimer}</div>
      )}
    </Page>
  );
}

export const BrandChevron = () => (
  <Chevron style={{ color: "var(--primary-500)" }} />
);

type StepProps = {
  step?: OnboardingStep;
  onAnswer: (answer?: Answer) => void;
};

const StepVariant = ({ step, onAnswer }: StepProps) => {
  if (!step) {
    return null;
  }

  if (step.component) {
    const Component = step.component;
    return (
      <div
        className={
          step.id !== "emailCollection" ? styles.variantWrapper : undefined
        }
      >
        <Component step={step} onAnswer={onAnswer} />
      </div>
    );
  }

  return (
    <div className={styles.variantWrapper}>
      {step.type === "multipleChoice" && (
        <MultipleChoiceStep step={step} onAnswer={onAnswer} />
      )}
      {step.type === "multiSelect" && (
        <MultiSelectStep step={step} onAnswer={onAnswer} />
      )}
      {step.type === "boolean" && <BooleanStep onAnswer={onAnswer} />}
      {step.type === "freetext" && (
        <FreetextStep step={step} onAnswer={onAnswer} />
      )}
      {step.type === "physicalStats" && (
        <PhysicalStatsStep onAnswer={onAnswer} />
      )}
      {step.type === "information" && <InformationStep onAnswer={onAnswer} />}
      {step.type === "disqualification" && (
        <DisqualificationVariants step={step} />
      )}
    </div>
  );
};

const MultipleChoiceStep = ({ step, onAnswer }: StepProps) => {
  const { answers, currentQuestionId } = useOnboarding();
  const device = useDevice();
  // Use a stale copy of the answers to prevent the UI from updating when selecting an answer the first time.
  const copiedAnswers = useStaleCopy(answers);

  return (
    <div className={styles.optionWrapper}>
      {step?.options?.map((option: Option, i: number) => {
        const answer =
          typeof option.value !== "undefined" ? option.value : option.text;

        if (option.clarification) {
          return (
            <ButtonWithDetails
              key={i}
              onClick={() => onAnswer(answer)}
              details={option.clarification}
            >
              {option.text}
            </ButtonWithDetails>
          );
        }

        return (
          <Button
            key={i}
            onClick={() => onAnswer(answer)}
            variant="secondary"
            iconRight={<BrandChevron />}
            fullWidth={device.isMobileOrTablet && step.fullWidthOptions}
            className={
              typeof copiedAnswers[currentQuestionId] !== "undefined" &&
              copiedAnswers[currentQuestionId] === answer
                ? styles.isAnswered
                : undefined
            }
          >
            {option.text}
          </Button>
        );
      })}
    </div>
  );
};

const MultiSelectStep = ({ step, onAnswer }: StepProps) => {
  const { currentQuestionId, toggleOption, multiSelections } = useOnboarding();
  const [validationError, setValidationError] = useState<string | undefined>();

  const confirmAnswers = () => {
    if (!multiSelections[currentQuestionId]?.length) {
      setValidationError("Vänligen välj minst ett av alternativen.");
      return;
    }

    onAnswer(multiSelections[currentQuestionId]);
  };

  return (
    <>
      <div className={styles.optionWrapper}>
        {step?.options?.map((option: Option, i: number) => {
          return (
            <Button
              key={i}
              onClick={() => toggleOption(option.text)}
              variant="secondary"
              iconRight={
                <CheckBox
                  readOnly
                  required
                  checked={
                    multiSelections[currentQuestionId]
                      ? multiSelections[currentQuestionId].includes(option.text)
                      : false
                  }
                />
              }
              fullWidth={step.fullWidthOptions}
            >
              {option.text}
            </Button>
          );
        })}
      </div>
      {validationError && (
        <div className={styles.validationError}>{validationError}</div>
      )}
      <div className={styles.formAction}>
        <Button onClick={confirmAnswers} iconRight={<Chevron />}>
          Nästa
        </Button>
      </div>
    </>
  );
};

const BooleanStep = ({ onAnswer }: StepProps) => {
  const { answers, currentQuestionId } = useOnboarding();
  const copiedAnswers = useStaleCopy(answers);

  return (
    <div className={styles.optionWrapper}>
      <Button
        variant="secondary"
        onClick={() => onAnswer(1)}
        iconRight={<BrandChevron />}
        className={
          copiedAnswers[currentQuestionId] === 1 ? styles.isAnswered : undefined
        }
      >
        Ja
      </Button>
      <Button
        variant="secondary"
        onClick={() => onAnswer(0)}
        iconRight={<BrandChevron />}
        className={
          copiedAnswers[currentQuestionId] === 0 ? styles.isAnswered : undefined
        }
      >
        Nej
      </Button>
    </div>
  );
};

const FreetextStep = ({ step, onAnswer }: StepProps) => {
  const { freetextData, setFreetextData } = useOnboarding();

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();

    if (!step) {
      return;
    }

    onAnswer(freetextData[step.id]);
  };

  const skipStep = (e: { preventDefault: () => void }) => {
    e.preventDefault();

    onAnswer();
  };

  if (!step) {
    return null;
  }

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className={styles.physicalStatsWrapper}>
          <label>
            {step?.fieldLabel}
            <input
              data-testid={step?.testID}
              type="text"
              value={freetextData[step.id] || ""}
              onChange={(e) => setFreetextData(step.id, e.target.value)}
              required={step.required}
            />
          </label>
        </div>
        <div className={styles.formAction}>
          <Button type="submit" iconRight={<Chevron />}>
            Nästa
          </Button>
        </div>
      </form>
      {step?.skippable && (
        <div className={styles.formAction} style={{ marginTop: "7px" }}>
          <Button
            variant="secondary"
            onClick={skipStep}
            iconRight={<BrandChevron />}
          >
            Hoppa över
          </Button>
        </div>
      )}
    </>
  );
};

const PhysicalStatsStep = ({ onAnswer }: StepProps) => {
  const {
    setPhysicalStats,
    height: storedHeight,
    weight: storedWeight,
  } = useOnboarding();
  const [height, setHeight] = useState(
    storedHeight ? storedHeight.toLocaleString() : "",
  );
  const [weight, setWeight] = useState(
    storedWeight ? storedWeight.toLocaleString() : "",
  );
  const MIN_WEIGHT = 40;
  const MAX_WEIGHT = 250;
  const MIN_HEIGHT = 100;
  const MAX_HEIGHT = 220;
  const [validationError, setValidationError] = useState<string>();

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();

    if (
      toNumber(height) > MAX_HEIGHT ||
      toNumber(weight) > MAX_WEIGHT ||
      toNumber(height) < MIN_HEIGHT ||
      toNumber(weight) < MIN_WEIGHT
    ) {
      setValidationError("Ange giltiga kroppsmått.");
      return;
    }

    setPhysicalStats(height, weight);
    const bmi = calculateBmi(toNumber(height), toNumber(weight));
    onAnswer(bmi);
  };

  return (
    <form onSubmit={handleSubmit} className="stack-1">
      <div className={styles.physicalStatsWrapper}>
        <div>
          <label htmlFor="height" className="unit" data-unit="cm">
            Längd
            <input
              id="height"
              type="text"
              inputMode="numeric"
              pattern="\d+[\.,]?\d{1}?"
              autoComplete="off"
              required
              value={height}
              onChange={(e) => {
                if (validationError) {
                  setValidationError(undefined);
                }
                setHeight(e.target.value);
              }}
            />
          </label>
        </div>
        <div>
          <label htmlFor="weight" className="unit" data-unit="kg">
            Vikt
            <input
              id="weight"
              type="text"
              inputMode="numeric"
              pattern="\d+[\.,]?\d{1}?"
              autoComplete="off"
              required
              value={weight}
              onChange={(e) => {
                if (validationError) {
                  setValidationError(undefined);
                }
                setWeight(e.target.value);
              }}
            />
          </label>
        </div>
      </div>
      {validationError && (
        <div className={styles.validationError}>{validationError}</div>
      )}
      <div className={styles.formAction}>
        <Button type="submit" iconRight={<Chevron />}>
          Nästa
        </Button>
      </div>
    </form>
  );
};

const DisqualificationVariants = ({ step }: Pick<StepProps, "step">) => {
  if (!step) {
    return;
  }

  return (
    <>
      {step?.disqualificationType === "lowRisk" && <DisqualificationLowRisk />}
      {step?.disqualificationType === "ageUnder35" && (
        <DisqualificationAge age={35} />
      )}
      {step?.disqualificationType === "ageUnder18" && (
        <DisqualificationAge age={18} />
      )}
      {step?.disqualificationType === "bmi" && <DisqualificationBmi />}
      {step?.disqualificationType === "noRiskRactors" && (
        <DisqualificationNoRiskFactors />
      )}
    </>
  );
};

const DisqualificationLowRisk = () => {
  return (
    <div>
      <p>
        <img src={bpImageSrc} alt="Blodtrycksmätningsstation" />
      </p>
      <h1>Mät blodtrycket vid en av våra Hälsostationer</h1>
      <p>
        Baserat på dina svar bedömer vi att din risk för högt blodtryck är lägre
        än vad vi normalt sett utreder.
      </p>

      <p>
        Det utesluter däremot inte att du kan ha högt blodtryck - därför
        rekommenderar vi att du mäter blodtrycket vid en av våra Hälsostationer
        som finns på över 140 Apotek Hjärtat runt om i landet.
      </p>

      <p className="text-center">
        <Button as="a" to={FIND_PHARMACY_URL} fullWidth iconRight={<Chevron />}>
          Hitta din närmaste Hälsostation
        </Button>
      </p>

      <div className={styles.inCollaboration}>
        I samarbete med{" "}
        <img src={apotekImageSrc} alt="Apotek Hjärtat" width={123} />
      </div>
    </div>
  );
};

const DisqualificationAge = ({ age }: { age: number }) => {
  return (
    <div>
      <p>
        Eftersom du är under {age} år kan vi tyvärr inte erbjuda dig behandling
        eller utredning för högt blodtryck.
      </p>
    </div>
  );
};

const DisqualificationBmi = () => {
  return null;
};

const DisqualificationNoRiskFactors = () => {
  return null;
};

const InformationStep = ({ onAnswer }: StepProps) => {
  return (
    <div className={styles.formAction}>
      <Button onClick={() => onAnswer()} iconRight={<Chevron />}>
        Nästa
      </Button>
    </div>
  );
};
